import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useMutation } from "react-relay";
import { toast } from "sonner";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/utils";
import type { featuresUpdateMutation } from "./__generated__/featuresUpdateMutation.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/features")({
	component: RouteComponent,
});

function RouteComponent() {
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const navigate = useNavigate({ from: Route.fullPath });
	const { capture, trackError } = useOnboardingAnalytics("features", clubSlug);

	const [updateClub] = useMutation<featuresUpdateMutation>(graphql`
    mutation featuresUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
      updateClub(id: $club, payload: $payload) {
        id
      }
    }
  `);

	const form = useForm({
		defaultValues: {
			coursesEnabled: true,
			communitiesEnabled: true,
			eventsEnabled: true,
			rankingEnabled: true,
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				updateClub({
					variables: {
						club: onboardingClub?.id ?? "",
						payload: {
							configurations: [
								{
									key: "COMMUNITY_ENABLED",
									value: value.communitiesEnabled ? "1" : "0",
								},
								{
									key: "COURSES_ENABLED",
									value: value.coursesEnabled ? "1" : "0",
								},
								{
									key: "EVENTS_ENABLED",
									value: value.eventsEnabled ? "1" : "0",
								},
								{
									key: "LEADERBOARD",
									value: value.rankingEnabled ? "ENABLED" : "DISABLED",
								},
								{
									key: "ONBOARDING_LAST_PAGE",
									value: "features",
								},
								...(!value.communitiesEnabled &&
								!value.coursesEnabled &&
								!value.eventsEnabled &&
								!value.rankingEnabled
									? [
											{
												key: "ONBOARDING_COMPLETED",
												value: "1",
											},
										]
									: []),
							],
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						capture("completed", {
							courses_enabled: value.coursesEnabled,
							communities_enabled: value.communitiesEnabled,
							events_enabled: value.eventsEnabled,
							ranking_enabled: value.rankingEnabled,
							features_count: [
								value.coursesEnabled,
								value.communitiesEnabled,
								value.eventsEnabled,
								value.rankingEnabled,
							].filter(Boolean).length,
						});
						toast.success("Configurações atualizadas com sucesso!");

						navigate({ to: "/onboarding/$club/cursos" });

						resolve(response);
					},
					onError: (error) => {
						trackError("submission", { error_message: error.message });
						toast.error("Erro ao atualizar configurações!");
						reject(error);
					},
				});
			});
		},
	});

	return (
		<form
			className="container mx-auto p-6 max-w-2xl flex flex-col"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<Card className="p-6 gap-0">
				<div>
					<Heading>Como você vai usar a Scarf?</Heading>

					<Text>
						Ative o que faz sentido agora. Você pode mudar isso depois.
					</Text>
				</div>

				<div className="mt-6 flex flex-col gap-3">
					<form.Field name="coursesEnabled">
						{(field) => (
							<button
								type="button"
								className={cn(
									"p-3 rounded-md flex flex-row gap-2 items-center justify-start border",
									field.state.value && "border-primary!",
								)}
								onClick={(ev) => {
									if (ev.target !== ev.currentTarget) {
										return;
									}
									field.setValue(!field.state.value);
								}}
							>
								<Checkbox
									className="pointer-events-none"
									checked={field.state.value}
								/>
								Cursos e Aulas
							</button>
						)}
					</form.Field>

					<form.Field name="communitiesEnabled">
						{(field) => (
							<button
								type="button"
								className={cn(
									"p-3 rounded-md flex flex-row gap-2 items-center justify-start border",
									field.state.value && "border-primary!",
								)}
								onClick={(ev) => {
									if (ev.target !== ev.currentTarget) {
										return;
									}
									field.setValue(!field.state.value);
								}}
							>
								<Checkbox
									className="pointer-events-none"
									checked={field.state.value}
								/>
								Comunidades e Feeds
							</button>
						)}
					</form.Field>

					<form.Field name="eventsEnabled">
						{(field) => (
							<button
								type="button"
								className={cn(
									"p-3 rounded-md flex flex-row gap-2 items-center justify-start border",
									field.state.value && "border-primary!",
								)}
								onClick={(ev) => {
									if (ev.target !== ev.currentTarget) {
										return;
									}
									field.setValue(!field.state.value);
								}}
							>
								<Checkbox
									className="pointer-events-none"
									checked={field.state.value}
								/>
								Eventos
							</button>
						)}
					</form.Field>

					<form.Field name="rankingEnabled">
						{(field) => (
							<button
								type="button"
								className={cn(
									"p-3 rounded-md flex flex-row gap-2 items-center justify-start border",
									field.state.value && "border-primary!",
								)}
								onClick={(ev) => {
									if (ev.target !== ev.currentTarget) {
										return;
									}
									field.setValue(!field.state.value);
								}}
							>
								<Checkbox
									className="pointer-events-none"
									checked={field.state.value}
								/>
								Ranking (Gamificação)
							</button>
						)}
					</form.Field>
				</div>

				<Separator className="mt-6" />

				<form.Subscribe selector={(state) => [state.isSubmitting]}>
					{([isSubmitting]) => (
						<div className="py-6 pb-0 flex items-center justify-between">
							<div className="flex items-start justify-start gap-3">
								<Button
									type="button"
									variant="outline"
									onClick={() =>
										navigate({
											to: "/onboarding/$club/features",
											params: { club: clubSlug },
										})
									}
									disabled={isSubmitting}
								>
									Voltar
								</Button>
							</div>

							<div className="flex items-center gap-3">
								<Button
									type="button"
									variant="ghost"
									onClick={() => {
										capture("skipped");
										navigate({
											to: "/onboarding/$club/feeds",
											params: { club: clubSlug },
										});
									}}
									disabled={isSubmitting}
								>
									Pular
								</Button>

								<Button
									type="submit"
									className="flex items-center gap-2"
									disabled={isSubmitting}
								>
									Continuar
								</Button>
							</div>
						</div>
					)}
				</form.Subscribe>
			</Card>
		</form>
	);
}
